import { SelectOption } from '@globaleris/ui.library'
import moment from 'moment'
import { EnumCommodity, EnumTipoDocumento, EnumTipoPeaje } from '../../api'
import { DocumentModel } from '../../api/Argentia/endpoints/documents/model/document'
import { NewProcedure, NewProcedureParameter } from '../../api/Argentia/endpoints/procedures'
import { AnnexedParameters, ProcedureStates, ProcedureTypes } from '../../api/Argentia/enums'
import { EnumPeriodo } from '../../api/Argentia/enums/periodo'
import {
  CUPSModel,
  CapacityModel,
  ContractATRModel,
  ContractModel,
  SubjectModel
} from '../../api/Argentia/model/contracts'
import {
  enumEnumToValueNumber,
  enumEnumToValueString,
  enumToSelectOptionArray,
  enumToSelectOptionArrayDefaultValues,
  removeDuplicatesByProperty
} from '../../helpers'
import { fileToBase64 } from '../../helpers/fileToBase64'
import { getActiveCompany } from '../../redux'
import { CapacityFormModel } from '../contracts/contracts.model'
import {
  AttachedDocumentFile,
  CupsProcedureForm,
  EnumAdministrativeRequestValues,
  EnumComplaintOwnerType,
  EnumComplaintType,
  EnumComplaintTypeInvoice,
  EnumComplaintTypeProcedure,
  EnumEffectiveDateValues,
  EnumEffectiveDateValuesPower,
  EnumGasStationTypeValues,
  EnumGasUsageValues,
  EnumProcedureProcess,
  EnumProcedureReason,
  EnumProcedureReasonChangeOwnerGas,
  EnumProcedureType,
  EnumProcedureTypeGas,
  EnumProcedureTypeHidden,
  EnumProcedureTypePower,
  EnumReasonTerminationGasValues,
  EnumReasonTerminationPowerValues,
  EnumRequestType,
  EnumResidential,
  NewProcedureRequestFormData,
  ProcedureFormData,
  ProcedureFormMode,
  ProcedureFormModeType
} from './procedures.types'

export const cupsModelToProcedureFormModelMapper = (cups: CUPSModel): CupsProcedureForm => {
  return {
    id: cups.id,
    cupsCode: cups.code,
    cnaeCode: cups.cnae,
    commodity: cups.commodity,
    pressureGroupId: cups.pressureGroupId
  }
}

export const contractModelToProcedureFormModelMapper = (contract: ContractModel): ContractModel => {
  return {
    ...contract,
    commodity: enumEnumToValueNumber(EnumCommodity, contract.commodity)!
  }
}

export const mapContractsToSelectOptions = (contracts: ContractModel[]): SelectOption[] => {
  return contracts && contracts.map(c => ({ label: c.contractCode, value: c.id }))
}

export const mapSubjectsToSelectOptions = (subjects: SubjectModel[]): SelectOption[] => {
  return subjects && subjects.map(c => ({ label: c.deSujeto, value: c.id }))
}

export const capacityModelListToFormModelMapper = (
  capacities: CapacityModel[]
): CapacityFormModel[] => {
  return capacities.map(capacity => capacityModelToFormModelMapper(capacity))
}

export const capacityListModelToFormModelMapper = (
  capacity: CapacityModel[]
): CapacityFormModel[] => {
  return capacity && capacity.map(capacityModelToFormModelMapper)
}

export const capacityModelToFormModelMapper = (capacity: CapacityModel): any => {
  return {
    // ...capacity,
    // rawMaterial: capacity.rawMaterial ?? false,
    // type: enumEnumToValueNumber(EnumTipoProductoCapacidad, capacity.type) ?? 0,
    // contractType: enumEnumToValueNumber(EnumServicio, capacity.contractType) ?? 0,
    toll: enumEnumToValueNumber(EnumTipoPeaje, capacity.toll) ?? 0,
    qdHired: capacity?.qdContratada ? +capacity.qdContratada : 0,
    qaEstimated: capacity?.qaEstimada ? +capacity.qaEstimada : 0
    // canEdit: true
  }
}

export const capacityModelToSelectOptionMapper = (capacities: CapacityModel[]): SelectOption[] => {
  return capacities.map(capacity => ({
    value: capacity.id!,
    label: capacity.code
  }))
}

export const atrModelToFormModelMapper = (contractATR: ContractATRModel): any => {
  const currentVersion = contractATR.versions.reduce((lastVersion, version) => {
    return (version.version ?? 0 > lastVersion.version) ? version : lastVersion
  })

  return {
    // ...atr,
    // rawMaterial: capacity.rawMaterial ?? false,
    // type: enumEnumToValueNumber(EnumTipoProductoCapacidad, capacity.type) ?? 0,
    // contractType: enumEnumToValueNumber(EnumServicio, capacity.contractType) ?? 0,
    toll: currentVersion.tollType,
    period1:
      currentVersion.powers?.find(power => power.period === EnumPeriodo.Periodo1)?.value ?? 0,
    period2:
      currentVersion.powers?.find(power => power.period === EnumPeriodo.Periodo2)?.value ?? 0,
    period3:
      currentVersion.powers?.find(power => power.period === EnumPeriodo.Periodo3)?.value ?? 0,
    period4:
      currentVersion.powers?.find(power => power.period === EnumPeriodo.Periodo4)?.value ?? 0,
    period5:
      currentVersion.powers?.find(power => power.period === EnumPeriodo.Periodo5)?.value ?? 0,
    period6: currentVersion.powers?.find(power => power.period === EnumPeriodo.Periodo6)?.value ?? 0
    // canEdit: true
  }
}

export const getProcedureTypeOptions = (
  data: ProcedureFormData,
  mode: ProcedureFormModeType = ProcedureFormMode.GENERAL
): SelectOption[] => {
  switch (mode) {
    case ProcedureFormMode.GENERAL: {
      const enumType =
        data.commodity === EnumCommodity.GasNatural ? EnumProcedureTypeGas : EnumProcedureTypePower

      return enumToSelectOptionArrayDefaultValues(enumType, 'enums:EnumProcedureType', [
        EnumProcedureTypeGas.ADMINISTRATIVE_CHANGE,
        EnumProcedureTypeGas.ADMINISTRATIVE_TECHNICAL_CHANGE_GAS,
        EnumProcedureTypeGas.CUPS,
        EnumProcedureTypeGas.NEW_CAPACITY,
        EnumProcedureTypePower.ADMINISTRATIVE_CHANGE_PWR,
        EnumProcedureTypePower.UPDATE_ATR,
        EnumProcedureTypePower.ADMINISTRATIVE_TECHNICAL_CHANGE_PWR,
        EnumProcedureTypePower.CUPS
      ])
    }
    case ProcedureFormMode.ALTA_CONTRATO_GAS: {
      return enumToSelectOptionArrayDefaultValues(EnumProcedureTypeGas, 'enums:EnumProcedureType', [
        EnumProcedureTypeGas.NEW_WITHOUT_CHANGES,
        EnumProcedureTypeGas.NEW_WITH_CHANGES,
        EnumProcedureTypeGas.NEW_SUPPLY
      ])
    }
    case ProcedureFormMode.ALTA_CONTRATO_POWER: {
      return enumToSelectOptionArrayDefaultValues(
        EnumProcedureTypePower,
        'enums:EnumProcedureType',
        [
          EnumProcedureTypePower.NEW_WITHOUT_CHANGES,
          EnumProcedureTypePower.NEW_WITH_CHANGES,
          EnumProcedureTypePower.NEW_SUPPLY
        ]
      )
    }
    case ProcedureFormMode.CAMBIO_TITULAR_GAS: {
      return enumToSelectOptionArrayDefaultValues(EnumProcedureType, 'enums:EnumProcedureType', [
        EnumProcedureType.UPDATE_OWNER_GAS
      ])
    }
    case ProcedureFormMode.CAMBIO_TITULAR_ADM_TEC_GAS: {
      return enumToSelectOptionArrayDefaultValues(EnumProcedureType, 'enums:EnumProcedureType', [
        EnumProcedureType.UPDATE_OWNER_ADM_TEC_GAS
      ])
    }

    case ProcedureFormMode.CAMBIO_TITULAR_POWER: {
      return enumToSelectOptionArrayDefaultValues(EnumProcedureType, 'enums:EnumProcedureType', [
        EnumProcedureType.UPDATE_OWNER_PWR
      ])
    }

    case ProcedureFormMode.CAMBIO_TITULAR_ADM_TEC_POWER: {
      return enumToSelectOptionArrayDefaultValues(EnumProcedureType, 'enums:EnumProcedureType', [
        EnumProcedureType.UPDATE_OWNER_ADM_TEC_PWR
      ])
    }

    case ProcedureFormMode.CAMBIO_CONTRATO_ATR: {
      return enumToSelectOptionArrayDefaultValues(
        EnumProcedureTypePower,
        'enums:EnumProcedureType',
        [
          EnumProcedureTypePower.UPDATE_ATR,
          EnumProcedureTypePower.ADMINISTRATIVE_TECHNICAL_CHANGE_PWR
        ]
      )
    }
    case ProcedureFormMode.BAJA_CONTRATO_GAS: {
      return enumToSelectOptionArrayDefaultValues(
        EnumProcedureTypeHidden,
        'enums:EnumProcedureType',
        [EnumProcedureTypeHidden.TERMINATION]
      )
    }
    case ProcedureFormMode.BAJA_CONTRATO_POWER: {
      return enumToSelectOptionArrayDefaultValues(
        EnumProcedureTypeHidden,
        'enums:EnumProcedureType',
        [EnumProcedureTypeHidden.TERMINATION]
      )
    }
    case ProcedureFormMode.SUSPENSION_CONTRATO_GAS: {
      return enumToSelectOptionArrayDefaultValues(EnumProcedureTypeGas, 'enums:EnumProcedureType', [
        EnumProcedureTypeGas.SUSPEND_SUPPLY_GAS
      ])
    }
    case ProcedureFormMode.RENGANCHE_CONTRATO_GAS: {
      return enumToSelectOptionArrayDefaultValues(EnumProcedureTypeGas, 'enums:EnumProcedureType', [
        EnumProcedureTypeGas.RECONNECT_SUPPLY_GAS
      ])
    }
    case ProcedureFormMode.SUSPENSION_CONTRATO_POWER: {
      return enumToSelectOptionArrayDefaultValues(
        EnumProcedureTypePower,
        'enums:EnumProcedureType',
        [EnumProcedureTypePower.SUSPEND_SUPPLY_PWR]
      )
    }
    case ProcedureFormMode.RECLAMACION_FACTURA: {
      return enumToSelectOptionArrayDefaultValues(
        EnumComplaintTypeInvoice,
        'enums:EnumComplaintTypeInvoice',
        [
          EnumComplaintTypeInvoice.COMPLAINT_INVOICE_GAS,
          EnumComplaintTypeInvoice.COMPLAINT_INVOICE_PWR
        ]
      )
    }
    case ProcedureFormMode.RECLAMACION_TRAMITACION: {
      return enumToSelectOptionArrayDefaultValues(
        EnumComplaintTypeProcedure,
        'enums:EnumComplaintTypeProcedure',
        [
          EnumComplaintTypeProcedure.COMPLAINT_PROCEDURE_GAS,
          EnumComplaintTypeProcedure.COMPLAINT_PROCEDURE_PWR
        ]
      )
    }
    case ProcedureFormMode.MODIFICACION_TECNICA: {
      return enumToSelectOptionArrayDefaultValues(
        EnumProcedureTypeHidden,
        'enums:EnumProcedureType',
        [EnumProcedureTypeHidden.TECHNICAL_CHANGE]
      )
    }
    default: {
      return enumToSelectOptionArray(
        data.commodity === EnumCommodity.GasNatural ? EnumProcedureTypeGas : EnumProcedureTypePower,
        'enums:EnumProcedureType'
      )
    }
  }
}

export const getProcedureTypeEnumByMode = (
  mode: ProcedureFormModeType = ProcedureFormMode.GENERAL
): EnumProcedureType | EnumProcedureTypeHidden | undefined => {
  switch (mode) {
    case ProcedureFormMode.CAMBIO_TITULAR_GAS: {
      return EnumProcedureType.UPDATE_OWNER_GAS
    }
    case ProcedureFormMode.CAMBIO_TITULAR_ADM_TEC_GAS: {
      return EnumProcedureType.UPDATE_OWNER_ADM_TEC_GAS
    }
    case ProcedureFormMode.CAMBIO_TITULAR_POWER: {
      return EnumProcedureType.UPDATE_OWNER_PWR
    }
    case ProcedureFormMode.CAMBIO_TITULAR_ADM_TEC_POWER: {
      return EnumProcedureType.UPDATE_OWNER_ADM_TEC_PWR
    }
    case ProcedureFormMode.CAMBIO_CONTRATO_ATR: {
      return EnumProcedureType.UPDATE_ATR
    }
    case ProcedureFormMode.BAJA_CONTRATO_GAS: {
      return EnumProcedureTypeHidden.TERMINATION
    }
    case ProcedureFormMode.BAJA_CONTRATO_POWER: {
      return EnumProcedureTypeHidden.TERMINATION
    }
    case ProcedureFormMode.ALTA_CAPACIDAD: {
      return EnumProcedureType.NEW_CAPACITY
    }
    case ProcedureFormMode.MODIFICACION_TECNICA: {
      return EnumProcedureTypeHidden.TECHNICAL_CHANGE
    }
    case ProcedureFormMode.SUSPENSION_CONTRATO_GAS: {
      return EnumProcedureType.SUSPEND_SUPPLY_GAS
    }
    case ProcedureFormMode.RENGANCHE_CONTRATO_GAS: {
      return EnumProcedureType.RECONNECT_SUPPLY_GAS
    }
    case ProcedureFormMode.SUSPENSION_CONTRATO_POWER: {
      return EnumProcedureType.SUSPEND_SUPPLY_PWR
    }
    default: {
      return undefined
    }
  }
}

export const getProcessTypeEnumByMode = (
  mode: ProcedureFormModeType = ProcedureFormMode.GENERAL
): EnumProcedureProcess | undefined => {
  switch (mode) {
    case ProcedureFormMode.ALTA_CONTRATO_GAS: {
      return EnumProcedureProcess.ALTA_CONTRATO_GAS
    }
    case ProcedureFormMode.ALTA_CONTRATO_POWER: {
      return EnumProcedureProcess.ALTA_CONTRATO_POWER
    }
    case ProcedureFormMode.CAMBIO_TITULAR_GAS: {
      return EnumProcedureProcess.CAMBIO_TITULAR_GAS
    }
    case ProcedureFormMode.CAMBIO_TITULAR_ADM_TEC_GAS: {
      return EnumProcedureProcess.CAMBIO_TITULAR_ADM_TEC_GAS
    }
    case ProcedureFormMode.CAMBIO_TITULAR_POWER: {
      return EnumProcedureProcess.CAMBIO_TITULAR_POWER
    }
    case ProcedureFormMode.CAMBIO_CONTRATO_ATR: {
      return EnumProcedureProcess.CAMBIO_CONTRATO_ATR
    }
    case ProcedureFormMode.BAJA_CONTRATO_GAS: {
      return EnumProcedureProcess.BAJA_CONTRATO_GAS
    }
    case ProcedureFormMode.BAJA_CONTRATO_POWER: {
      return EnumProcedureProcess.BAJA_CONTRATO_POWER
    }
    case ProcedureFormMode.SUSPENSION_CONTRATO_GAS: {
      return EnumProcedureProcess.SUSPENSION_CONTRATO_GAS
    }
    case ProcedureFormMode.SUSPENSION_CONTRATO_POWER: {
      return EnumProcedureProcess.SUSPENSION_CONTRATO_POWER
    }
    case ProcedureFormMode.RENGANCHE_CONTRATO_GAS: {
      return EnumProcedureProcess.REENGANCHE_CONTRATO_GAS
    }
    case ProcedureFormMode.RECLAMACION_FACTURA: {
      return undefined // would be defined later on getInvoiceDetails, so that commodity is known
    }
    case ProcedureFormMode.RECLAMACION_TRAMITACION: {
      return undefined // would be defined later on getProcedureDetails, so that commodity is known
    }
    case ProcedureFormMode.MODIFICACION_TECNICA: {
      return EnumProcedureProcess.MODIFICACION_TECNICA
    }
    default: {
      return EnumProcedureProcess.GENERAL
    }
  }
}

export const getReasonOptionsByPressure = (highPressure: boolean): SelectOption[] => {
  if (highPressure) {
    return enumToSelectOptionArray(EnumProcedureReason, 'enums:EnumProcedureReason')
  }
  return enumToSelectOptionArray(EnumProcedureReasonChangeOwnerGas, 'enums:EnumProcedureReason')
}

export const getParameters = (procedure: ProcedureFormData): NewProcedure | null => {
  if (procedure.commodity === EnumCommodity.GasNatural) {
    switch (procedure.type) {
      case EnumProcedureType.NEW_WITHOUT_CHANGES: {
        return mapNewProcedureWithoutChanges(procedure as NewProcedureRequestFormData)
      }
      case EnumProcedureType.NEW_WITH_CHANGES: {
        return mapNewProcedureWithChanges(procedure as NewProcedureRequestFormData)
      }
      case EnumProcedureType.NEW_SUPPLY: {
        return mapNewProcedureNewSupply(procedure as NewProcedureRequestFormData)
      }
      case EnumProcedureType.UPDATE_OWNER_GAS:
      case EnumProcedureType.UPDATE_OWNER_ADM_TEC_GAS: {
        return mapNewProcedureChangeOwnerGas(procedure as NewProcedureRequestFormData)
      }
      case EnumProcedureTypeHidden.TERMINATION: {
        return mapNewProcedureTerminationGas(procedure as NewProcedureRequestFormData)
      }
      case EnumProcedureTypeGas.SUSPEND_SUPPLY_GAS: {
        return mapNewProcedureSuspendAndReconnectGas(procedure as NewProcedureRequestFormData)
      }
      case EnumProcedureTypeGas.RECONNECT_SUPPLY_GAS: {
        return mapNewProcedureSuspendAndReconnectGas(procedure as NewProcedureRequestFormData)
      }
      case EnumProcedureTypeGas.CUPS: {
        return mapNewComplaintGas(
          procedure as NewProcedureRequestFormData,
          EnumProcedureTypeGas.CUPS
        )
      }
      case EnumComplaintTypeInvoice.COMPLAINT_INVOICE_GAS: {
        return mapNewComplaintGas(
          procedure as NewProcedureRequestFormData,
          EnumComplaintTypeInvoice.COMPLAINT_INVOICE_GAS
        )
      }
      case EnumComplaintTypeProcedure.COMPLAINT_PROCEDURE_GAS: {
        return mapNewComplaintGas(
          procedure as NewProcedureRequestFormData,
          EnumComplaintTypeProcedure.COMPLAINT_PROCEDURE_GAS
        )
      }
      case EnumProcedureType.NEW_CAPACITY: {
        return mapNewProcedurenNewCapacity(procedure as NewProcedureRequestFormData)
      }
      case EnumProcedureType.ADMINISTRATIVE_CHANGE: {
        return mapNewModificationChangesProcedure(
          procedure as NewProcedureRequestFormData,
          EnumProcedureType.ADMINISTRATIVE_CHANGE
        )
      }
      case EnumProcedureType.ADMINISTRATIVE_TECHNICAL_CHANGE_GAS: {
        return mapNewModificationChangesProcedure(
          procedure as NewProcedureRequestFormData,
          EnumProcedureType.ADMINISTRATIVE_TECHNICAL_CHANGE_GAS
        )
      }
      case EnumProcedureTypeHidden.TECHNICAL_CHANGE: {
        return mapNewModificationChangesProcedure(
          procedure as NewProcedureRequestFormData,
          EnumProcedureTypeHidden.TECHNICAL_CHANGE
        )
      }
    }
  } else if (procedure.commodity === EnumCommodity.Electricidad) {
    switch (procedure.type) {
      case EnumProcedureType.NEW_WITHOUT_CHANGES: {
        return mapNewProcedureWithoutChangesPower(procedure as NewProcedureRequestFormData)
      }
      case EnumProcedureType.NEW_WITH_CHANGES: {
        return mapNewProcedureWithChangesPower(procedure as NewProcedureRequestFormData)
      }
      case EnumProcedureType.NEW_SUPPLY: {
        return mapNewProcedureNewSupplyPower(procedure as NewProcedureRequestFormData)
      }
      case EnumProcedureType.UPDATE_ATR: {
        return mapNewProcedureUpdateATR(procedure as NewProcedureRequestFormData)
      }
      case EnumProcedureType.ADMINISTRATIVE_CHANGE_PWR:
      case EnumProcedureType.ADMINISTRATIVE_TECHNICAL_CHANGE_PWR:
      case EnumProcedureType.UPDATE_OWNER_PWR:
      case EnumProcedureType.UPDATE_OWNER_ADM_TEC_PWR: {
        return mapNewProcedureAdministrativeChangePower(procedure as NewProcedureRequestFormData)
      }
      case EnumProcedureTypeHidden.TERMINATION: {
        return mapNewProcedureTerminationPower(procedure as NewProcedureRequestFormData)
      }
      case EnumProcedureType.SUSPEND_SUPPLY_PWR: {
        return mapNewProcedureTerminationPower(procedure as NewProcedureRequestFormData)
      }
      case EnumProcedureTypePower.CUPS: {
        return mapNewComplaintPower(
          procedure as NewProcedureRequestFormData,
          EnumProcedureTypePower.CUPS
        )
      }
      case EnumComplaintTypeInvoice.COMPLAINT_INVOICE_PWR: {
        return mapNewComplaintPower(
          procedure as NewProcedureRequestFormData,
          EnumComplaintTypeInvoice.COMPLAINT_INVOICE_PWR
        )
      }
      case EnumComplaintTypeProcedure.COMPLAINT_PROCEDURE_PWR: {
        return mapNewComplaintPower(
          procedure as NewProcedureRequestFormData,
          EnumComplaintTypeProcedure.COMPLAINT_PROCEDURE_PWR
        )
      }
    }
  }

  return null
}

export const mapNewProcedureWithoutChanges = (
  formData: NewProcedureRequestFormData
): NewProcedure => {
  const type: ProcedureTypes = ProcedureTypes.CambioSINCambios
  let parameters: NewProcedureParameter[] = [
    {
      type: AnnexedParameters.MODELO_FECHA_EFECTO,
      value: enumEnumToValueString(EnumEffectiveDateValues, formData.efectiveDate) ?? ''
    },
    {
      type: AnnexedParameters.ALTA_CONDICIONADA,
      value: formData.conditionalRegistration ? 'S' : 'N'
    },
    {
      type: AnnexedParameters.CONSUMO_ANUAL,
      value: formData.qaEstimated?.toString() ?? ''
    }
  ]

  if (formData.date) {
    parameters.push({
      type: AnnexedParameters.FH_EFECTO,
      value: moment(formData.date).format('YYYY-MM-DD')
    })
  }
  if (formData.cnaeCode) {
    parameters.push({
      type: AnnexedParameters.CNAE,
      value: formData.cnaeCode
    })
  }
  if (formData.essentialIndicatorGas) {
    parameters.push({
      type: AnnexedParameters.GAS_CLIENTE_ESENCIAL,
      value: formData.essentialIndicatorGas
    })
  }
  if (formData.protectedIndicator) {
    parameters.push({
      type: AnnexedParameters.GAS_CLIENTE_PROTEGIDO,
      value: formData.protectedIndicator
    })
  }
  if (formData.comments) {
    parameters.push({ type: AnnexedParameters.COMENTARIOS, value: formData.comments })
  }

  return {
    code: '',
    cups: formData.cupsCode,
    type: type ?? 1,
    state: ProcedureStates.Iniciado,
    date: moment().toDate(),
    parameters: parameters
  }
}

const mapNewProcedureWithOwnerChange = (
  formData: NewProcedureRequestFormData
): NewProcedureParameter[] => {
  return [
    {
      type: AnnexedParameters.MODELO_FECHA_EFECTO,
      value: enumEnumToValueString(EnumEffectiveDateValues, formData.efectiveDate) ?? ''
    },
    {
      type: AnnexedParameters.ALTA_CONDICIONADA,
      value: formData.conditionalRegistration ? 'S' : 'N'
    },
    {
      type: AnnexedParameters.SUBROGACION,
      value: formData.subrogation ? 'S' : 'N'
    },
    {
      type: AnnexedParameters.VIVIENDA_HABITUAL,
      value: formData.principalResidence ? 'S' : 'N'
    }
  ]
}

const mapNewProcedureWithVolumeChange = (
  formData: NewProcedureRequestFormData
): NewProcedureParameter[] => {
  return [
    {
      type: AnnexedParameters.MODELO_FECHA_EFECTO,
      value: enumEnumToValueString(EnumEffectiveDateValues, formData.efectiveDate) ?? ''
    },
    {
      type: AnnexedParameters.ALTA_CONDICIONADA,
      value: formData.conditionalRegistration ? 'S' : 'N'
    },
    {
      type: AnnexedParameters.Q_DIARIO,
      value: formData.qdHired?.toString() ?? ''
    }
  ]
}

const mapNewProcedureParamsNewSupply = (
  formData: NewProcedureRequestFormData
): NewProcedureParameter[] => {
  const params = [
    {
      type: AnnexedParameters.MODELO_FECHA_EFECTO,
      value: enumEnumToValueString(EnumEffectiveDateValues, formData.efectiveDate) ?? ''
    },
    {
      type: AnnexedParameters.USO_GAS,
      value: enumEnumToValueString(EnumGasUsageValues, formData?.gasUsage) ?? ''
    },
    {
      type: AnnexedParameters.PROPIEDAD_CONTADOR,
      value: formData?.meterProperty?.toString().padStart(2, '0') ?? ''
    },
    {
      type: AnnexedParameters.VIVIENDA_HABITUAL,
      value: formData.principalResidence ? 'S' : 'N'
    },
    {
      type: AnnexedParameters.TRANSFORMACION_APARATOS,
      value: formData.deviceTransfer !== 99 ? 'S' : 'N'
    },
    {
      type: AnnexedParameters.CONSUMO_ANUAL,
      value: formData.qaEstimated?.toString() ?? ''
    }
  ]

  if (formData.requestedPressure) {
    params.push({
      type: AnnexedParameters.PRESION_SOLICITADA,
      value: formData.requestedPressure.toString()
    })
  }

  if (formData.gasStationType) {
    const value = enumEnumToValueString(EnumGasStationTypeValues, formData?.gasStationType)
    value &&
      params.push({
        type: AnnexedParameters.TIPO_GASINERA,
        value: value
      })
  }

  if (formData.deviceTransfer && formData.deviceTransfer !== 99) {
    params.push({
      type: AnnexedParameters.NUM_APARATOS_TRANSFORMAR,
      value: formData.deviceTransfer.toString()
    })
  }

  return params
}

export const mapNewProcedureWithChanges = (formData: NewProcedureRequestFormData): NewProcedure => {
  let parameters: NewProcedureParameter[] = []
  let type: ProcedureTypes | null = null

  switch (formData.reason) {
    case EnumProcedureReason.CHANGE_OWNER: {
      type = ProcedureTypes.CambioCONCambios_Titular
      parameters = mapNewProcedureWithOwnerChange(formData)
      break
    }
    case EnumProcedureReason.CHANGE_VOLUME: {
      type = ProcedureTypes.CambioCONCambios_Ampliacion_Caudal
      parameters = mapNewProcedureWithVolumeChange(formData)
      break
    }
    case EnumProcedureReason.BOTH: {
      type = ProcedureTypes.CambioCONCambios_Titular_y_Caudal
      parameters = removeDuplicatesByProperty(
        [...mapNewProcedureWithOwnerChange(formData), ...mapNewProcedureWithVolumeChange(formData)],
        'type'
      )
      break
    }
  }

  if (formData.date) {
    parameters.push({
      type: AnnexedParameters.FH_EFECTO,
      value: moment(formData.date).format('YYYY-MM-DD')
    })
  }

  if (formData.cnaeCode) {
    parameters.push({
      type: AnnexedParameters.CNAE,
      value: formData.cnaeCode
    })
  }
  if (formData.essentialIndicatorGas) {
    parameters.push({
      type: AnnexedParameters.GAS_CLIENTE_ESENCIAL,
      value: formData.essentialIndicatorGas
    })
  }
  if (formData.protectedIndicator) {
    parameters.push({
      type: AnnexedParameters.GAS_CLIENTE_PROTEGIDO,
      value: formData.protectedIndicator
    })
  }
  if (formData.comments) {
    parameters.push({ type: AnnexedParameters.COMENTARIOS, value: formData.comments })
  }

  return {
    code: '',
    cups: formData.cupsCode,
    type: type ?? 1,
    state: ProcedureStates.Iniciado,
    date: moment().toDate(),
    parameters: parameters
  }
}

export const mapNewProcedureNewSupply = (formData: NewProcedureRequestFormData): NewProcedure => {
  let parameters: NewProcedureParameter[] = []
  let type: ProcedureTypes | null = null

  type = ProcedureTypes.Alta_Nuevo_Suministro
  parameters = mapNewProcedureParamsNewSupply(formData)

  if (formData.date) {
    parameters.push({
      type: AnnexedParameters.FH_EFECTO,
      value: moment(formData.date).format('YYYY-MM-DD')
    })
  }

  if (formData.cnaeCode) {
    parameters.push({
      type: AnnexedParameters.CNAE,
      value: formData.cnaeCode
    })
  }
  if (formData.essentialIndicatorGas) {
    parameters.push({
      type: AnnexedParameters.GAS_CLIENTE_ESENCIAL,
      value: formData.essentialIndicatorGas
    })
  }
  if (formData.protectedIndicator) {
    parameters.push({
      type: AnnexedParameters.GAS_CLIENTE_PROTEGIDO,
      value: formData.protectedIndicator
    })
  }
  if (formData.comments) {
    parameters.push({ type: AnnexedParameters.COMENTARIOS, value: formData.comments })
  }

  return {
    code: '',
    cups: formData.cupsCode,
    type: type ?? 1,
    state: ProcedureStates.Iniciado,
    date: moment().toDate(),
    parameters: parameters
  }
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////// MAPPERS COMMODITY POWER //////////////////////////////////////////////////

const getCNAEParamValue = (residential?: EnumResidential): string | null => {
  switch (residential) {
    case EnumResidential.PRIMARY_RESIDENCE:
      return '9820'
    case EnumResidential.SECOND_RESIDENCE:
      return '9821'
    case EnumResidential.COMMUNITY:
      return '9810'
    default:
      return null
  }
}

export const mapNewProcedureWithoutChangesPower = (
  formData: NewProcedureRequestFormData
): NewProcedure => {
  const type: ProcedureTypes = ProcedureTypes.CambioSINCambios_PWR
  let parameters: NewProcedureParameter[] = [
    {
      type: AnnexedParameters.MODELO_FECHA_EFECTO,
      value: enumEnumToValueString(EnumEffectiveDateValuesPower, formData.efectiveDate) ?? ''
    },
    {
      type: AnnexedParameters.ALTA_CONDICIONADA,
      value: formData.conditionalRegistration ? 'S' : 'N'
    },
    {
      type: AnnexedParameters.BONO_SOCIAL,
      value: formData.socialSubsidy ? 'S' : 'N'
    }
  ]

  if (formData.date) {
    parameters.push({
      type: AnnexedParameters.FH_EFECTO,
      value: moment(formData.date).format('YYYY-MM-DD')
    })
  }

  if (formData.essentialIndicator) {
    parameters.push({
      type: AnnexedParameters.PWR_CLIENTE_ESENCIAL,
      value: formData.essentialIndicator
    })
  }

  if (formData.essentialMovementDate) {
    parameters.push({
      type: AnnexedParameters.FH_MOVIMIENTO_ESENCIAL,
      value: moment(formData.essentialMovementDate).format('YYYY-MM-DD')
    })
  }

  if (formData.comments) {
    parameters.push({ type: AnnexedParameters.COMENTARIOS, value: formData.comments })
  }

  return {
    code: '',
    cups: formData.cupsCode,
    type: type ?? 1,
    state: ProcedureStates.Iniciado,
    date: moment().toDate(),
    parameters: parameters
  }
}

const mapNewProcedureWithAdminChange = (
  formData: NewProcedureRequestFormData
): NewProcedureParameter[] => {
  const params = [
    {
      type: AnnexedParameters.MODELO_FECHA_EFECTO,
      value: enumEnumToValueString(EnumEffectiveDateValuesPower, formData.efectiveDate) ?? ''
    },
    {
      type: AnnexedParameters.ALTA_CONDICIONADA,
      value: formData.conditionalRegistration ? 'S' : 'N'
    },
    {
      type: AnnexedParameters.BONO_SOCIAL,
      value: formData.socialSubsidy ? 'S' : 'N'
    },
    {
      type: AnnexedParameters.MOTIVO_MODIFICACION_PWR,
      value: formData.administrativeRequest
        ? enumEnumToValueString(EnumAdministrativeRequestValues, formData.administrativeRequest)!
        : ''
    }
  ]

  const cnae = getCNAEParamValue(formData?.residential) ?? formData.cnaeCode ?? ''

  if (cnae) {
    params.push({
      type: AnnexedParameters.CNAE,
      value: cnae
    })
  }

  return params
}

const mapNewProcedureWithTechnicalChange = (
  formData: NewProcedureRequestFormData
): NewProcedureParameter[] => {
  let parameters = [
    {
      type: AnnexedParameters.MODELO_FECHA_EFECTO,
      value: enumEnumToValueString(EnumEffectiveDateValuesPower, formData.efectiveDate) ?? ''
    },
    {
      type: AnnexedParameters.ALTA_CONDICIONADA,
      value: formData.conditionalRegistration ? 'S' : 'N'
    },
    {
      type: AnnexedParameters.BONO_SOCIAL,
      value: formData.socialSubsidy ? 'S' : 'N'
    }
  ]
  if (formData.tension) {
    parameters.push({
      type: AnnexedParameters.TENSION_SOLICITADA,
      value: formData.tension ? getTensionValue(formData.tension) : ''
    })
  }

  return parameters
}

export const mapNewProcedureWithChangesPower = (
  formData: NewProcedureRequestFormData
): NewProcedure => {
  let parameters: NewProcedureParameter[] = []
  let type: ProcedureTypes | null = null

  switch (formData.requestType) {
    case EnumRequestType.ADMINISTRATIVE: {
      type = ProcedureTypes.CambioCONCambios_PWR_Adm
      parameters = mapNewProcedureWithAdminChange(formData)
      break
    }
    case EnumRequestType.TECHNICAL: {
      type = ProcedureTypes.CambioCONCambios_PWR_Tec
      parameters = mapNewProcedureWithTechnicalChange(formData)
      break
    }
    case EnumRequestType.BOTH: {
      type = ProcedureTypes.CambioCONCambios_PWR_Adm_y_Tec
      parameters = removeDuplicatesByProperty(
        [
          ...mapNewProcedureWithAdminChange(formData),
          ...mapNewProcedureWithTechnicalChange(formData)
        ],
        'type'
      )
      break
    }
  }

  if (formData.date) {
    parameters.push({
      type: AnnexedParameters.FH_EFECTO,
      value: moment(formData.date).format('YYYY-MM-DD')
    })
  }

  if (formData.essentialIndicator) {
    parameters.push({
      type: AnnexedParameters.PWR_CLIENTE_ESENCIAL,
      value: formData.essentialIndicator
    })
  }

  if (formData.essentialMovementDate) {
    parameters.push({
      type: AnnexedParameters.FH_MOVIMIENTO_ESENCIAL,
      value: moment(formData.essentialMovementDate).format('YYYY-MM-DD')
    })
  }

  if (formData.comments) {
    parameters.push({ type: AnnexedParameters.COMENTARIOS, value: formData.comments })
  }

  return {
    code: '',
    cups: formData.cupsCode,
    type: type ?? 1,
    state: ProcedureStates.Iniciado,
    date: moment().toDate(),
    parameters: parameters
  }
}

const mapNewProcedureParamsNewSupplyPower = (
  formData: NewProcedureRequestFormData
): NewProcedureParameter[] => {
  const params = [
    {
      type: AnnexedParameters.MODELO_FECHA_EFECTO,
      value: enumEnumToValueString(EnumEffectiveDateValuesPower, formData.efectiveDate) ?? ''
    }
  ]

  const cnae = getCNAEParamValue(formData?.residential) ?? formData.cnaeCode ?? ''

  if (cnae) {
    params.push({
      type: AnnexedParameters.CNAE,
      value: cnae
    })
  }

  if (formData.tension) {
    params.push({
      type: AnnexedParameters.TENSION_SOLICITADA,
      value: getTensionValue(formData.tension)
    })
  }

  if (formData.deviceTransfer && formData.deviceTransfer !== 99) {
    params.push({
      type: AnnexedParameters.NUM_APARATOS_TRANSFORMAR,
      value: formData.deviceTransfer.toString()
    })
  }

  return params
}

export const mapNewProcedureNewSupplyPower = (
  formData: NewProcedureRequestFormData
): NewProcedure => {
  let parameters: NewProcedureParameter[] = []
  let type: ProcedureTypes | null = null

  type = ProcedureTypes.Alta_Nuevo_Suministro_PWR
  parameters = mapNewProcedureParamsNewSupplyPower(formData)

  if (formData.date) {
    parameters.push({
      type: AnnexedParameters.FH_EFECTO,
      value: moment(formData.date).format('YYYY-MM-DD')
    })
  }

  if (formData.essentialIndicator) {
    parameters.push({
      type: AnnexedParameters.PWR_CLIENTE_ESENCIAL,
      value: formData.essentialIndicator
    })
  }

  if (formData.essentialMovementDate) {
    parameters.push({
      type: AnnexedParameters.FH_MOVIMIENTO_ESENCIAL,
      value: moment(formData.essentialMovementDate).format('YYYY-MM-DD')
    })
  }

  if (formData.comments) {
    parameters.push({ type: AnnexedParameters.COMENTARIOS, value: formData.comments })
  }

  return {
    code: '',
    cups: formData.cupsCode,
    type: type ?? 1,
    state: ProcedureStates.Iniciado,
    date: moment().toDate(),
    parameters: parameters
  }
}

const getTensionValue = (tension: number): string => {
  if (tension < 10) {
    return '0' + tension.toString()
  } else {
    return tension.toString()
  }
}

export const mapNewProcedureUpdateATR = (formData: NewProcedureRequestFormData): NewProcedure => {
  let parameters: NewProcedureParameter[] = []
  let type: ProcedureTypes | null = null

  type = ProcedureTypes.Modificacion_PWR_Tec
  parameters = mapNewProcedureParamsNewSupplyPower(formData)

  if (formData.date) {
    parameters.push({
      type: AnnexedParameters.FH_EFECTO,
      value: moment(formData.date).format('YYYY-MM-DD')
    })
  }

  if (formData.comments) {
    parameters.push({ type: AnnexedParameters.COMENTARIOS, value: formData.comments })
  }

  return {
    code: '',
    cups: formData.cupsCode,
    type: type ?? 1,
    state: ProcedureStates.Iniciado,
    date: moment().toDate(),
    parameters: parameters
  }
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////// MAPPERS CAMBIO DE TITULAR //////////////////////////////////////////////////

const mapNewProcedureParamsChangeOwnerGas = (
  formData: NewProcedureRequestFormData
): NewProcedureParameter[] => {
  const params = [
    {
      type: AnnexedParameters.MODELO_FECHA_EFECTO,
      value: enumEnumToValueString(EnumEffectiveDateValues, formData.efectiveDate) ?? ''
    },
    {
      type: AnnexedParameters.SUBROGACION,
      value: formData.subrogation ? 'S' : 'N'
    },
    {
      type: AnnexedParameters.VIVIENDA_HABITUAL,
      value: formData.principalResidence ? 'S' : 'N'
    }
  ]

  return params
}

export const mapNewProcedureChangeOwnerGas = (
  formData: NewProcedureRequestFormData
): NewProcedure => {
  const type =
    formData.context === EnumProcedureProcess.CAMBIO_TITULAR_ADM_TEC_GAS
      ? ProcedureTypes.Modificacion_Titular_y_Tec
      : ProcedureTypes.Modificacion_Titular

  let parameters: NewProcedureParameter[] = []

  parameters = mapNewProcedureParamsChangeOwnerGas(formData)

  if (formData.admChangeReasons) {
    parameters.push({
      type: AnnexedParameters.MOTIVO_MODIFICACION,
      value: formData.admChangeReasons
    })
  }

  if (formData.newDate) {
    parameters.push({
      type: AnnexedParameters.FH_EFECTO,
      value: moment(formData.newDate).format('YYYY-MM-DD')
    })
  }

  if (formData.comments) {
    parameters.push({ type: AnnexedParameters.COMENTARIOS, value: formData.comments })
  }

  if (formData.cnaeCode) {
    parameters.push({
      type: AnnexedParameters.CNAE,
      value: formData.cnaeCode
    })
  }
  if (formData.essentialIndicatorGas) {
    parameters.push({
      type: AnnexedParameters.GAS_CLIENTE_ESENCIAL,
      value: formData.essentialIndicatorGas
    })
  }
  if (formData.protectedIndicator) {
    parameters.push({
      type: AnnexedParameters.GAS_CLIENTE_PROTEGIDO,
      value: formData.protectedIndicator
    })
  }

  return {
    code: '',
    cups: formData.cupsCode,
    type: type ?? 1,
    state: ProcedureStates.Iniciado,
    date: moment().toDate(),
    parameters: parameters
  }
}

export const mapNewProcedureAdministrativeChangePower = (
  formData: NewProcedureRequestFormData
): NewProcedure => {
  let parameters: NewProcedureParameter[] = []

  parameters.push({
    type: AnnexedParameters.MOTIVO_MODIFICACION_PWR,
    value: formData.changeReason ?? ''
  })

  parameters.push({
    type: AnnexedParameters.MODELO_FECHA_EFECTO,
    value: enumEnumToValueString(EnumEffectiveDateValuesPower, formData.efectiveDate) ?? ''
  })

  if (formData.residential) {
    const cnae = getCNAEParamValue(formData?.residential) ?? formData.cnaeCode ?? ''

    parameters.push({
      type: AnnexedParameters.CNAE,
      value: cnae
    })
  }

  if (formData.date) {
    parameters.push({
      type: AnnexedParameters.FH_EFECTO,
      value: moment(formData.date).format('YYYY-MM-DD')
    })
  }

  if (formData.newDate) {
    parameters.push({
      type: AnnexedParameters.FH_EFECTO,
      value: moment(formData.newDate).format('YYYY-MM-DD')
    })
  }

  if (formData.essentialIndicator) {
    parameters.push({
      type: AnnexedParameters.PWR_CLIENTE_ESENCIAL,
      value: formData.essentialIndicator
    })
  }

  if (formData.essentialMovementDate) {
    parameters.push({
      type: AnnexedParameters.FH_MOVIMIENTO_ESENCIAL,
      value: moment(formData.essentialMovementDate).format('YYYY-MM-DD')
    })
  }

  if (formData.tension) {
    parameters.push({
      type: AnnexedParameters.TENSION_SOLICITADA,
      value: formData.tension ? getTensionValue(formData.tension) : ''
    })
  }

  if (formData.billingFrequency) {
    parameters.push({
      type: AnnexedParameters.PERIODICIDAD_FACTURACION,
      value: formData.billingFrequency
    })
  }

  if (formData.comments) {
    parameters.push({ type: AnnexedParameters.COMENTARIOS, value: formData.comments })
  }

  const getModificationType = (): ProcedureTypes => {
    switch (formData.type) {
      case EnumProcedureType.UPDATE_OWNER_PWR:
        return ProcedureTypes.Modificacion_Titular_PWR
      case EnumProcedureType.UPDATE_OWNER_ADM_TEC_PWR:
        return ProcedureTypes.Modificacion_Titular_y_Tec_PWR
      case EnumProcedureType.ADMINISTRATIVE_TECHNICAL_CHANGE_PWR:
        return ProcedureTypes.Modificacion_PWR_Adm_y_Tec
      default:
        return ProcedureTypes.Modificacion_PWR_Adm
    }
  }

  return {
    code: '',
    cups: formData.cupsCode,
    type: getModificationType(),
    state: ProcedureStates.Iniciado,
    date: moment().toDate(),
    parameters: parameters
  }
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////// MAPPERS BAJA DE SUMINISTRO/////////////////////////////////////////////////

const mapNewProcedureParamsTerminationGas = (
  formData: NewProcedureRequestFormData
): NewProcedureParameter[] => {
  const params = [
    {
      type: AnnexedParameters.MODELO_FECHA_EFECTO,
      value: enumEnumToValueString(EnumEffectiveDateValues, formData.efectiveDate) ?? ''
    },
    {
      type: AnnexedParameters.MOTIVO_BAJA,
      value: enumEnumToValueString(EnumReasonTerminationGasValues, formData.reasonTermination) ?? ''
    }
  ]

  return params
}

export const mapNewProcedureTerminationGas = (
  formData: NewProcedureRequestFormData
): NewProcedure => {
  let parameters: NewProcedureParameter[] = []
  let type: ProcedureTypes | null = null

  type = ProcedureTypes.Baja_Suministro_GAS
  parameters = mapNewProcedureParamsTerminationGas(formData)

  if (formData.date) {
    parameters.push({
      type: AnnexedParameters.FH_EFECTO,
      value: moment(formData.date).format('YYYY-MM-DD')
    })
  }
  if (formData.comments) {
    parameters.push({ type: AnnexedParameters.COMENTARIOS, value: formData.comments })
  }

  return {
    code: '',
    cups: formData.cupsCode,
    type: type ?? 1,
    state: ProcedureStates.Iniciado,
    date: moment().toDate(),
    parameters: parameters
  }
}

export const mapNewProcedureSuspendAndReconnectGas = (
  formData: NewProcedureRequestFormData
): NewProcedure => {
  const isSuspension = formData.context === EnumProcedureProcess.SUSPENSION_CONTRATO_GAS

  const type = isSuspension
    ? ProcedureTypes.CorteSuministroGas
    : ProcedureTypes.ReengancheSuministroGas

  const parameters: NewProcedureParameter[] = [
    {
      type: AnnexedParameters.GAS_TIPO_OPERACION,
      value: formData.reasonGasSuspension?.toString() ?? ''
    }
  ]

  if (formData.comments) {
    parameters.push({
      type: AnnexedParameters.COMENTARIOS,
      value: formData.comments
    })
  }

  return {
    code: '',
    cups: formData.cupsCode,
    ...(formData.originProcedureId && { originProcedureId: formData.originProcedureId }),
    type,
    state: ProcedureStates.Iniciado,
    date: moment().toDate(),
    parameters
  }
}

const mapNewProcedureParamsTerminationPower = (
  formData: NewProcedureRequestFormData
): NewProcedureParameter[] => {
  const params = [
    {
      type: AnnexedParameters.MODELO_FECHA_EFECTO,
      value: enumEnumToValueString(EnumEffectiveDateValuesPower, formData.efectiveDate) ?? ''
    },
    {
      type: AnnexedParameters.MOTIVO_BAJA,
      value:
        enumEnumToValueString(EnumReasonTerminationPowerValues, formData.reasonTermination) ?? ''
    }
  ]

  return params
}

export const mapNewProcedureTerminationPower = (
  formData: NewProcedureRequestFormData
): NewProcedure => {
  let parameters: NewProcedureParameter[] = []
  let type: ProcedureTypes | null = null

  type =
    formData.type === EnumProcedureType.SUSPEND_SUPPLY_PWR
      ? ProcedureTypes.CorteSuministroPWR
      : ProcedureTypes.Baja_Suministro_PWR
  parameters = mapNewProcedureParamsTerminationPower(formData)

  if (formData.date) {
    parameters.push({
      type: AnnexedParameters.FH_EFECTO,
      value: moment(formData.date).format('YYYY-MM-DD')
    })
  }

  if (formData.comments) {
    parameters.push({ type: AnnexedParameters.COMENTARIOS, value: formData.comments })
  }

  return {
    code: '',
    cups: formData.cupsCode,
    type: type ?? 1,
    state: ProcedureStates.Iniciado,
    date: moment().toDate(),
    parameters: parameters
  }
}

export const mapAttachedDocumentFilesToDocuments = async (
  documents: AttachedDocumentFile[]
): Promise<DocumentModel[]> => {
  const documentsPromise = documents.map(document =>
    mapAttachedDocumentFileItemToDocument(document)
  )
  return Promise.all(documentsPromise)
}

export const mapAttachedDocumentFileItemToDocument = async (
  document: AttachedDocumentFile
): Promise<DocumentModel> => {
  const fileContent = await fileToBase64(document.content)
  const type = enumEnumToValueNumber(EnumTipoDocumento, document.type) ?? 0
  const date = new Date()
  const activeCompany = getActiveCompany()?.id ?? 0 // if null, API should give error
  return {
    description: document.description,
    documentDate: moment(date).format('YYYY-MM-DD'),
    type,
    comments: document.comments,
    companyID: activeCompany,
    attachment: {
      contenido: fileContent,
      blobName: document.content.name
    }
  }
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////// MAPPERS CUPS/INVOICE/PROCEDURES COMPLAINT /////////////////////////////////////////////////
//#region complaint mappers starts

export const mapAnnexedParam = (
  id: EnumComplaintType | EnumComplaintOwnerType,
  responseData
): SelectOption[] => {
  const foundParam = responseData.find(param => param.id === id)
  if (!foundParam) {
    return []
  }
  return foundParam.annexedParamsValues.map(option => ({
    value: option.code,
    label: option.description
  }))
}

export const mapNewComplaintGas = (formData: NewProcedureRequestFormData, type): NewProcedure => {
  let parameters: NewProcedureParameter[] = [
    {
      type: AnnexedParameters.GAS_TIPO_RECLAMACION,
      value: formData.complaintType?.toString()!
    },
    {
      type: AnnexedParameters.GAS_SUBTIPO_RECLAMACION,
      value: formData.complaintSubType!
    },
    {
      type: AnnexedParameters.GAS_TIPO_RECLAMANTE,
      value: formData.complaintOwnerType?.toString()!
    },
    {
      type: AnnexedParameters.PRIORITARIO,
      value: formData.priority ? '1' : '0'
    },
    // COMMENTS FIELD must appear only when form input has value
    ...(formData.comments
      ? [
          {
            type: AnnexedParameters.COMENTARIOS,
            value: formData.comments
          }
        ]
      : []),
    // STARTDATE FIELD must appear only when form input has value
    ...(formData.startDate
      ? [
          {
            type: AnnexedParameters.FH_DESDE,
            value: moment(formData.startDate).format('YYYY-MM-DD')
          }
        ]
      : []),
    // ENDATE FIELD must appear only when form input has value
    ...(formData.endDate
      ? [
          {
            type: AnnexedParameters.FH_HASTA,
            value: moment(formData.endDate).format('YYYY-MM-DD')
          }
        ]
      : []),
    // DEADLINE FIELD must appear only when form input has value
    ...(formData.deadline
      ? [
          {
            type: AnnexedParameters.FECHA_LIMITE,
            value: moment(formData.deadline).format('YYYY-MM-DD')
          }
        ]
      : []),
    // CONTRACT CONCEPT FIELD must appear only when form input has value
    ...(formData.contractConcept
      ? [
          {
            type: AnnexedParameters.GAS_CONCEPTO_CONTRATACION,
            value: formData.contractConcept
          }
        ]
      : []),
    // INVOICE CONCEPT FIELD must appear only when form input has value
    ...(formData.invoiceConcept
      ? [
          {
            type: AnnexedParameters.GAS_CONCEPTO_FACTURACION,
            value: formData.invoiceConcept
          }
        ]
      : [])
  ]

  return {
    code: '',
    cups: formData.cupsCode,
    ...(formData.invoiceId && { invoiceId: formData.invoiceId }),
    ...(formData.originProcedureId && { originProcedureId: formData.originProcedureId }),
    type: type!,
    state: ProcedureStates.Iniciado,
    date: moment().toDate(),
    parameters: parameters
  }
}

export const mapNewComplaintPower = (formData: NewProcedureRequestFormData, type): NewProcedure => {
  let parameters: NewProcedureParameter[] = [
    {
      type: AnnexedParameters.PWR_TIPO_RECLAMACION,
      value: formData.complaintType?.toString()!
    },
    {
      type: AnnexedParameters.PWR_SUBTIPO_RECLAMACION,
      value: formData.complaintSubType!
    },
    {
      type: AnnexedParameters.PWR_TIPO_RECLAMANTE,
      value: formData.complaintOwnerType?.toString()!
    },
    {
      type: AnnexedParameters.PRIORITARIO,
      value: formData.priority ? 'S' : 'N'
    },
    // COMMENTS FIELD must appear only when form input has value
    ...(formData.comments
      ? [
          {
            type: AnnexedParameters.COMENTARIOS,
            value: formData.comments
          }
        ]
      : []),
    // STARTDATE FIELD must appear only when form input has value
    ...(formData.startDate
      ? [
          {
            type: AnnexedParameters.FH_DESDE,
            value: moment(formData.startDate).format('YYYY-MM-DD')
          }
        ]
      : []),
    // ENDATE FIELD must appear only when form input has value
    ...(formData.endDate
      ? [
          {
            type: AnnexedParameters.FH_HASTA,
            value: moment(formData.endDate).format('YYYY-MM-DD')
          }
        ]
      : []),
    // DEADLINE FIELD must appear only when form input has value
    ...(formData.deadline
      ? [
          {
            type: AnnexedParameters.FECHA_LIMITE,
            value: moment(formData.deadline).format('YYYY-MM-DD')
          }
        ]
      : []),
    // CONTRACT CONCEPT FIELD must appear only when form input has value
    ...(formData.contractConcept
      ? [
          {
            type: AnnexedParameters.PWR_CONCEPTO_CONTRATACION,
            value: formData.contractConcept
          }
        ]
      : []),
    // INVOICE CONCEPT FIELD must appear only when form input has value
    ...(formData.invoiceConcept
      ? [
          {
            type: AnnexedParameters.PWR_CONCEPTO_FACTURACION,
            value: formData.invoiceConcept
          }
        ]
      : []),
    // INCIDENT CODE FIELD must appear only when form input has value (subtype = 003 && commodity == Power)
    ...(formData.incidentCode
      ? [
          {
            type: AnnexedParameters.CODIGO_INCIDENCIA,
            value: formData.incidentCode
          }
        ]
      : [])
  ]

  return {
    code: '',
    cups: formData.cupsCode,
    ...(formData.invoiceId && { invoiceId: formData.invoiceId }),
    ...(formData.originProcedureId && { originProcedureId: formData.originProcedureId }),
    type: type!,
    state: ProcedureStates.Iniciado,
    date: moment().toDate(),
    parameters: parameters
  }
}

//#endregion cups mappers ends

//////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////// MAPPERS NEW_CAPACITY /////////////////////////////////////////////////
//#region new_capacity mappers starts

export const mapNewProcedurenNewCapacity = (
  formData: NewProcedureRequestFormData
): NewProcedure => {
  let parameters: NewProcedureParameter[] = [
    {
      type: AnnexedParameters.MODELO_FECHA_EFECTO,
      value: enumEnumToValueString(EnumEffectiveDateValues, formData.efectiveDate) ?? ''
    },
    // Date FIELD must appear only when form input has value
    ...(formData.date
      ? [
          {
            type: AnnexedParameters.FH_EFECTO,
            value: moment(formData.date).format('YYYY-MM-DD')
          }
        ]
      : []),
    // COMMENTS FIELD must appear only when form input has value
    ...(formData.comments
      ? [
          {
            type: AnnexedParameters.COMENTARIOS,
            value: formData.comments
          }
        ]
      : [])
  ]

  return {
    code: '',
    cups: formData.cupsCode,
    capacityId: formData.capacityId!,
    type: ProcedureTypes.NuevaContratacionAcceso_GAS,
    state: ProcedureStates.Iniciado,
    date: moment().toDate(),
    parameters: parameters
  }
}

//#endregion new_capacity mappers ends

//////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////// MAPPERS ADMINISTRATIVE_CHANGES /////////////////////////////////////////////////
//#region administrative and technical changes mappers starts

export const mapNewModificationChangesProcedure = (
  formData: NewProcedureRequestFormData,
  formType: number
): NewProcedure => {
  // Obligatory Parameters
  let parameters: NewProcedureParameter[] = [
    {
      type: AnnexedParameters.MODELO_FECHA_EFECTO,
      value: enumEnumToValueString(EnumEffectiveDateValues, formData.efectiveDate) ?? ''
    },
    {
      type: AnnexedParameters.MOTIVO_MODIFICACION,
      value: formData.admChangeReasons!
    }
  ]

  // Optional Parameters
  if (formData.subrogation)
    parameters.push({
      type: AnnexedParameters.SUBROGACION,
      value: formData.subrogation ? 'S' : 'N'
    })
  if (formData.principalResidence)
    parameters.push({
      type: AnnexedParameters.VIVIENDA_HABITUAL,
      value: formData.principalResidence ? 'S' : 'N'
    })
  if (formData.date)
    parameters.push({
      type: AnnexedParameters.FH_EFECTO,
      value: moment(formData.date).format('YYYY-MM-DD')
    })
  if (formData.comments)
    parameters.push({ type: AnnexedParameters.COMENTARIOS, value: formData.comments })
  if (formData.cnaeCode) {
    parameters.push({
      type: AnnexedParameters.CNAE,
      value: formData.cnaeCode
    })
  }
  if (formData.essentialIndicatorGas) {
    parameters.push({
      type: AnnexedParameters.GAS_CLIENTE_ESENCIAL,
      value: formData.essentialIndicatorGas
    })
  }
  if (formData.protectedIndicator) {
    parameters.push({
      type: AnnexedParameters.GAS_CLIENTE_PROTEGIDO,
      value: formData.protectedIndicator
    })
  }

  return {
    code: '',
    cups: formData.cupsCode,
    ...(formType === EnumProcedureTypeHidden.TECHNICAL_CHANGE && {
      capacityId: formData.capacityId!
    }),
    type:
      formType === EnumProcedureType.ADMINISTRATIVE_TECHNICAL_CHANGE_GAS
        ? ProcedureTypes.Modificacion_GAS_Adm_y_Tec
        : formType === EnumProcedureType.ADMINISTRATIVE_CHANGE
          ? ProcedureTypes.Modificacion_GAS_Adm
          : ProcedureTypes.Modificacion_GAS_Tec,
    state: ProcedureStates.Iniciado,
    date: moment().toDate(),
    parameters: parameters
  }
}

//#endregion administrative_changes mappers ends
